import { ReminderMode, ReminderPeriod } from "@/types/reminder.types.ts";

export interface ReminderPreset {
  frequency: number;
  period: ReminderPeriod;
}

export const regularReminderPresets: ReminderPreset[] = [
  { frequency: 1, period: ReminderPeriod.day },
  { frequency: 2, period: ReminderPeriod.day },
  { frequency: 3, period: ReminderPeriod.day },
  { frequency: 1, period: ReminderPeriod.week },
  { frequency: 2, period: ReminderPeriod.week },
];

export const reminderOffChoice = "off";
const reminderPresetPrefix = "preset";

export function encodeReminderPreset(frequency: number, period: ReminderPeriod): string {
  return `${reminderPresetPrefix}:${frequency}:${period}`;
}

export function decodeReminderPreset(choice: string): ReminderPreset {
  const [, frequency, period] = choice.split(":");
  return { frequency: Number(frequency), period: period as ReminderPeriod };
}

export interface ReminderPayload {
  reminder_mode: ReminderMode | null;
  reminder_custom_frequency?: number | null;
  reminder_custom_period?: ReminderPeriod | null;
}

export function buildReminderPayload(
  choice: string | null,
  customFrequency: number,
  customPeriod: ReminderPeriod
): ReminderPayload {
  if (choice == null || choice === reminderOffChoice) {
    return { reminder_mode: null };
  }
  if (
    choice === ReminderMode.light ||
    choice === ReminderMode.medium ||
    choice === ReminderMode.hard
  ) {
    return { reminder_mode: choice };
  }
  if (choice === ReminderMode.custom) {
    return {
      reminder_mode: ReminderMode.custom,
      reminder_custom_frequency: customFrequency,
      reminder_custom_period: customPeriod,
    };
  }
  const { frequency, period } = decodeReminderPreset(choice);
  return {
    reminder_mode: ReminderMode.custom,
    reminder_custom_frequency: frequency,
    reminder_custom_period: period,
  };
}

export interface ReminderChoiceState {
  choice: string | null;
  customFrequency: number;
  customPeriod: ReminderPeriod;
}

export function reminderChoiceFromPayload(payload: ReminderPayload): ReminderChoiceState {
  const fallback: ReminderChoiceState = {
    choice: null,
    customFrequency: 1,
    customPeriod: ReminderPeriod.day,
  };

  const mode = payload.reminder_mode;
  if (mode == null) return fallback;

  if (mode === ReminderMode.light || mode === ReminderMode.medium || mode === ReminderMode.hard) {
    return { ...fallback, choice: mode };
  }

  const frequency = payload.reminder_custom_frequency ?? 1;
  const period = payload.reminder_custom_period ?? ReminderPeriod.day;
  const matchedPreset = regularReminderPresets.find(
    (preset) => preset.frequency === frequency && preset.period === period
  );
  if (matchedPreset) {
    return { ...fallback, choice: encodeReminderPreset(frequency, period) };
  }

  return { choice: ReminderMode.custom, customFrequency: frequency, customPeriod: period };
}
